// Raw-document download through the EXPORT-scope-gated backend endpoint (066 B / option A).
//
// Viewing a document uses the direct `file_url` (open in a tab / inline preview). The explicit
// "Download" affordance instead fetches the file through `ClinicalDocumentViewSet.download`,
// which returns 403 if the document is not in the user's export scope — so a consortium member
// who can VIEW every centre's patients still cannot download another institution's source files
// via the app. The fetch is authenticated (it goes through the `api` axios instance, which adds
// the Bearer token), unlike a bare `window.open(file_url)`.
import { AxiosError } from "axios";
import api from "api/api";

export class DownloadForbiddenError extends Error {}

/**
 * Download a document by its gated `download_url` (absolute URL to
 * `/api/patient-documents/{id}/download/`). Triggers a browser "save" of the returned bytes.
 * Throws {@link DownloadForbiddenError} on a 403 (out of export scope) so the caller can show a
 * clear message; re-throws other errors.
 */
export async function downloadDocumentFromUrl(
  downloadUrl: string,
  fileName?: string
): Promise<void> {
  // `api` already has baseURL "/api"; strip the origin + "/api" prefix so we pass the path.
  const path = downloadUrl.replace(/^https?:\/\/[^/]+\/api/, "").replace(/^\/api/, "");
  try {
    const resp = await api.get(path, { responseType: "blob" });
    const blobUrl = URL.createObjectURL(resp.data as Blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    if (error instanceof AxiosError && error.response?.status === 403) {
      throw new DownloadForbiddenError();
    }
    throw error;
  }
}
